export interface PracticeTypeData {
  name: string;
  slug: string;
  title: string;
  metaDescription: string;
  heroTitle: string;
  heroSubtitle: string;
  description: string;
  specificRisks: {
    icon: string;
    title: string;
    description: string;
    statistic: string;
  }[];
  recommendedCoverage: {
    coverage: string;
    amount: string;
    importance: "essential" | "recommended" | "optional";
    description: string;
  }[];
  caseStudies: {
    title: string;
    description: string;
    damage: string;
    solution: string;
  }[];
  faqs: {
    question: string;
    answer: string;
  }[];
  priceRange: string;
  employeeRange: string;
  practiceCount: string;
}

export const practiceTypes: Record<string, PracticeTypeData> = {
  hausarzt: {
    name: "Hausarztpraxen",
    slug: "hausarzt",
    title: "Cyberversicherung für Hausarztpraxen - Patientendaten sicher schützen",
    metaDescription: "Spezialisierte Cyberversicherung für Hausarztpraxen ab 29€/Monat. Schutz vor Ransomware, Datenverlust und DSGVO-Bußgeldern. Jetzt unverbindlich beraten lassen.",
    heroTitle: "Cyberversicherung für Hausarztpraxen",
    heroSubtitle: "Umfassender Schutz für Ihre Patientendaten und Praxissoftware ab 29€/Monat",
    description: "Als Hausarztpraxis verwalten Sie täglich sensible Gesundheitsdaten von hunderten Patienten. Ein Cyberangriff kann nicht nur zu hohen Kosten führen, sondern auch das Vertrauen Ihrer Patienten gefährden. Unsere spezialisierte Cyberversicherung schützt Ihre Praxis umfassend.",
    specificRisks: [
      {
        icon: "FileX",
        title: "Praxissoftware-Ausfall",
        description: "Ransomware verschlüsselt Ihre Praxissoftware. Patientenakten, Terminkalender und Abrechnungssysteme sind nicht mehr zugänglich.",
        statistic: "Ø 7 Tage Praxisstillstand"
      },
      {
        icon: "Database",
        title: "Patientendaten-Diebstahl",
        description: "Hacker stehlen Krankenakten, Diagnosen und Medikationspläne Ihrer Patienten. DSGVO-Bußgelder und Schadensersatzforderungen drohen.",
        statistic: "Bis zu 20 Mio. € Bußgeld"
      },
      {
        icon: "Mail",
        title: "Phishing-Angriffe",
        description: "Mitarbeiter öffnen präparierte E-Mails. Schadsoftware verbreitet sich im gesamten Praxisnetzwerk und kompromittiert alle Systeme.",
        statistic: "92% aller Cyberangriffe"
      },
      {
        icon: "Clock",
        title: "Betriebsunterbrechung",
        description: "Durch IT-Ausfall können keine Patienten behandelt werden. Umsatzverlust, fixe Kosten laufen weiter, Patienten weichen zu anderen Praxen aus.",
        statistic: "Ø 15.000€ Umsatzverlust/Woche"
      },
      {
        icon: "Shield",
        title: "Cyber-Erpressung",
        description: "Kriminelle drohen mit Veröffentlichung sensibler Patientendaten oder permanenter Datenlöschung und fordern Lösegeld.",
        statistic: "Ø 25.000€ Lösegeldforderung"
      },
      {
        icon: "AlertTriangle",
        title: "Datenschutzverletzungen",
        description: "Meldepflicht bei Datenpannen innerhalb 72h. Anwaltskosten, Behördenverfahren und Patienteninformationen verursachen hohe Folgekosten.",
        statistic: "Ø 8.500€ pro Vorfall"
      }
    ],
    recommendedCoverage: [
      {
        coverage: "Cyber-Eigenschaden",
        amount: "100.000 - 250.000 €",
        importance: "essential",
        description: "Datenwiederherstellung, Forensik, IT-Notfallkosten"
      },
      {
        coverage: "Betriebsunterbrechung",
        amount: "50.000 - 150.000 €",
        importance: "essential",
        description: "Umsatzausfall während IT-Ausfall, fixe Kosten"
      },
      {
        coverage: "Cyber-Haftpflicht",
        amount: "1.000.000 - 3.000.000 €",
        importance: "essential",
        description: "DSGVO-Bußgelder, Schadensersatz Dritter"
      },
      {
        coverage: "Cyber-Erpressung",
        amount: "25.000 - 100.000 €",
        importance: "recommended",
        description: "Lösegeldzahlungen, Verhandlungskosten"
      },
      {
        coverage: "Krisenmanagement",
        amount: "25.000 - 50.000 €",
        importance: "recommended",
        description: "PR-Beratung, Patientenkommunikation"
      },
      {
        coverage: "Rechtsschutz Cyber",
        amount: "50.000 - 100.000 €",
        importance: "optional",
        description: "Anwaltskosten bei Cyber-Rechtsstreitigkeiten"
      }
    ],
    caseStudies: [
      {
        title: "Hausarztpraxis München - Ransomware-Angriff",
        description: "Eine Hausarztpraxis mit 2.800 Patienten wurde durch Ransomware lahmgelegt. Alle Patientenakten waren verschlüsselt.",
        damage: "42.000 € Gesamtschaden (IT-Forensik, Datenrettung, 9 Tage Praxisausfall, Patienteninformation)",
        solution: "Die Cyberversicherung übernahm sämtliche Kosten inkl. Betriebsunterbrechung und stellte IT-Notfallexperten bereit."
      },
      {
        title: "Landpraxis Brandenburg - Phishing-Betrug",
        description: "Durch gefälschte E-Mail einer 'KV' wurde Schadsoftware installiert. Patientendaten wurden gestohlen.",
        damage: "28.500 € (DSGVO-Meldung, Anwaltskosten, Patientenbenachrichtigung, Datenschutzbeauftragter)",
        solution: "Versicherung deckte alle Kosten und organisierte Datenschutz-Schulungen für das Praxisteam."
      }
    ],
    faqs: [
      {
        question: "Was kostet eine Cyberversicherung für meine Hausarztpraxis?",
        answer: "Die Kosten hängen von Faktoren wie Praxisgröße, IT-Ausstattung und gewünschter Deckungssumme ab. Für eine typische Hausarztpraxis mit 2-5 Mitarbeitern liegen die Beiträge zwischen 29€ und 89€ pro Monat. Größere Praxen mit mehr Mitarbeitern zahlen entsprechend mehr."
      },
      {
        question: "Welche Deckungssumme benötige ich als Hausarzt?",
        answer: "Wir empfehlen mindestens 100.000€ für Eigenschäden und 1.000.000€ für Haftpflichtschäden. Bei mehr als 2.000 Patienten sollten die Summen auf 250.000€ bzw. 3.000.000€ erhöht werden, um auch bei schweren Datenpannen ausreichend geschützt zu sein."
      },
      {
        question: "Sind auch Schäden durch Mitarbeiterfehler versichert?",
        answer: "Ja, gute Cyberversicherungen decken auch fahrlässige Handlungen Ihrer Mitarbeiter ab, z.B. wenn versehentlich ein Phishing-Link angeklickt wird oder ein USB-Stick mit Schadsoftware verwendet wird."
      },
      {
        question: "Wie schnell greift die Versicherung im Schadensfall?",
        answer: "Bei einem Cyberangriff erhalten Sie innerhalb von 2-4 Stunden eine 24/7-Notfall-Hotline. IT-Forensiker und Datenrettungsexperten werden sofort aktiviert, um Ihre Praxis schnellstmöglich wieder arbeitsfähig zu machen."
      },
      {
        question: "Muss ich bestimmte IT-Sicherheitsmaßnahmen vorweisen?",
        answer: "Grundlegende Maßnahmen wie regelmäßige Backups, aktuelle Antivirensoftware und Firewalls werden vorausgesetzt. Die meisten Versicherer bieten aber auch Unterstützung bei der Implementierung dieser Maßnahmen an."
      },
      {
        question: "Sind auch Cloud-basierte Praxissysteme versichert?",
        answer: "Ja, moderne Cyberversicherungen decken sowohl lokale als auch cloudbasierte Systeme ab. Auch Schäden durch Ausfall von Cloud-Diensten oder Datenverlust in der Cloud sind versichert."
      }
    ],
    priceRange: "29-89",
    employeeRange: "2-8",
    practiceCount: "42.000+"
  },
  
  zahnarzt: {
    name: "Zahnarztpraxen",
    slug: "zahnarzt",
    title: "Cyberversicherung für Zahnarztpraxen - Röntgendaten & Abrechnungen schützen",
    metaDescription: "Spezialisierte Cyberversicherung für Zahnarztpraxen ab 35€/Monat. Schutz für digitale Röntgenbilder, Abrechnungssysteme und Patientendaten. Jetzt beraten lassen.",
    heroTitle: "Cyberversicherung für Zahnarztpraxen",
    heroSubtitle: "Spezieller Schutz für digitale Röntgensysteme und Abrechnungssoftware ab 35€/Monat",
    description: "Zahnarztpraxen arbeiten mit hochsensiblen digitalen Systemen: Röntgenbilder, 3D-Scans, digitale Abformungen und komplexe Abrechnungssoftware. Ein Cyberangriff kann den gesamten Praxisbetrieb zum Erliegen bringen und hohe Kosten verursachen.",
    specificRisks: [
      {
        icon: "FileImage",
        title: "Digitale Röntgendaten-Verlust",
        description: "Ransomware verschlüsselt Röntgenbilder, DVT-Aufnahmen und digitale Abformungen. Behandlungen können nicht fortgesetzt werden.",
        statistic: "Ø 12.000€ Wiederherstellungskosten"
      },
      {
        icon: "CreditCard",
        title: "Abrechnungssystem-Ausfall",
        description: "Ihr Abrechnungssystem für Privat- und Kassenpatienten fällt aus. Honorarabrechnungen verzögern sich, Cashflow-Probleme entstehen.",
        statistic: "Ø 25.000€ Umsatzverlust/Monat"
      },
      {
        icon: "Server",
        title: "Praxissoftware-Kompromittierung",
        description: "Behandlungspläne, Heil- und Kostenpläne sowie Patientenakten werden durch Schadsoftware beschädigt oder gestohlen.",
        statistic: "78% höheres Risiko als Hausarztpraxen"
      },
      {
        icon: "Wifi",
        title: "Netzwerk-Angriffe auf Geräte",
        description: "Moderne Zahnarztpraxen mit vernetzten Geräten (Röntgen, CAD/CAM, Intraoralscanner) bieten mehr Angriffsfläche für Hacker.",
        statistic: "Ø 8 vernetzte Geräte pro Praxis"
      },
      {
        icon: "Euro",
        title: "Zahlungsverkehr-Manipulation",
        description: "Cyberkriminelle manipulieren Überweisungen oder stehlen Zahlungsdaten von Privatpatienten beim Online-Banking.",
        statistic: "Ø 18.000€ Schaden"
      },
      {
        icon: "UserX",
        title: "Labor-Kommunikation gestört",
        description: "Verschlüsselte oder gehackte E-Mail-Systeme unterbrechen die digitale Kommunikation mit Dentallaboren.",
        statistic: "Ø 5 Tage Behandlungsverzögerung"
      }
    ],
    recommendedCoverage: [
      {
        coverage: "Cyber-Eigenschaden",
        amount: "150.000 - 300.000 €",
        importance: "essential",
        description: "Speziell für teure Röntgen- und CAD/CAM-Systeme"
      },
      {
        coverage: "Betriebsunterbrechung",
        amount: "75.000 - 200.000 €",
        importance: "essential",
        description: "Höhere Umsätze als Hausarztpraxen berücksichtigen"
      },
      {
        coverage: "Cyber-Haftpflicht",
        amount: "1.500.000 - 5.000.000 €",
        importance: "essential",
        description: "Schutz vor hohen DSGVO-Strafen bei Röntgendaten"
      },
      {
        coverage: "Datenträger-Wiederherstellung",
        amount: "50.000 - 100.000 €",
        importance: "recommended",
        description: "Speziell für digitale Bilddaten und 3D-Scans"
      },
      {
        coverage: "Cyber-Erpressung",
        amount: "50.000 - 150.000 €",
        importance: "recommended",
        description: "Zahnarztpraxen oft Ziel wegen hoher Umsätze"
      },
      {
        coverage: "Vertragsstrafen",
        amount: "25.000 - 75.000 €",
        importance: "optional",
        description: "Bei Verzug von Zahntechnik-Aufträgen"
      }
    ],
    caseStudies: [
      {
        title: "Zahnarztpraxis Hamburg - DVT-System gehackt",
        description: "Ransomware verschlüsselte sämtliche 3D-Röntgenaufnahmen eines Implantologie-Schwerpunkts. 47 laufende Behandlungen betroffen.",
        damage: "68.000 € (IT-Forensik, Datenrettung, 14 Tage Teilausfall, Neuplanung Implantate)",
        solution: "Versicherung stellte spezialisierte Forensiker für medizinische Bildsysteme und übernahm Behandlungsverzögerungskosten."
      },
      {
        title: "Gemeinschaftspraxis Berlin - Abrechnungs-Hack",
        description: "Hacker manipulierten das Abrechnungssystem und leiteten Privathonorar-Überweisungen um.",
        damage: "35.000 € (gestohlene Zahlungen, Forensik, neue Sicherheitssysteme, Patienteninformation)",
        solution: "Cyberversicherung erstattete gestohlene Beträge und finanzierte IT-Sicherheitsupgrade."
      }
    ],
    faqs: [
      {
        question: "Warum brauchen Zahnarztpraxen eine spezielle Cyberversicherung?",
        answer: "Zahnarztpraxen arbeiten mit besonders sensiblen und wertvollen digitalen Daten: Röntgenbilder, 3D-Scans, CAD/CAM-Daten und haben oft höhere Umsätze. Zudem sind mehr vernetzte Geräte im Einsatz, was mehr Angriffsfläche bietet. Eine Standard-Cyberversicherung deckt diese speziellen Risiken oft nicht ausreichend ab."
      },
      {
        question: "Was kostet eine Cyberversicherung für Zahnarztpraxen?",
        answer: "Für eine Einzelpraxis mit 3-6 Mitarbeitern liegen die Beiträge zwischen 35€ und 120€ monatlich. Größere Gemeinschaftspraxen oder Praxen mit Implantologie-Schwerpunkt (höhere Umsätze, mehr digitale Systeme) zahlen zwischen 150€ und 300€ pro Monat."
      },
      {
        question: "Sind auch externe Dentallabore mitversichert?",
        answer: "Die Versicherung deckt Schäden durch Datenverlust oder -diebstahl in der Kommunikation mit Laboren ab. Allerdings sind die Labore selbst nicht mitversichert - diese benötigen eine eigene Cyberversicherung."
      },
      {
        question: "Was passiert, wenn Röntgenbilder durch Cyberangriff verloren gehen?",
        answer: "Die Versicherung übernimmt die Kosten für Datenrettung durch spezialisierte Forensiker. Falls Daten nicht wiederherstellbar sind, werden auch Kosten für Neuaufnahmen und Behandlungsverzögerungen erstattet. Zudem unterstützt die Versicherung bei der Patientenkommunikation."
      },
      {
        question: "Wie hoch sollte die Deckungssumme für eine Zahnarztpraxis sein?",
        answer: "Wir empfehlen mindestens 150.000€ für Eigenschäden und 1.500.000€ für Haftpflicht. Bei Praxen mit DVT, CAD/CAM oder Intraoralscannern sollten die Summen auf 300.000€ bzw. 5.000.000€ erhöht werden."
      },
      {
        question: "Sind auch Cloud-basierte Praxissysteme und Röntgenspeicher versichert?",
        answer: "Ja, moderne Cyberversicherungen decken sowohl lokale als auch cloudbasierte Speicherlösungen ab. Auch Schäden durch Ausfall von Cloud-Diensten oder Datenverlust bei externen Servern sind versichert."
      }
    ],
    priceRange: "35-120",
    employeeRange: "3-12",
    practiceCount: "28.000+"
  },
  
  mvz: {
    name: "Medizinische Versorgungszentren (MVZ)",
    slug: "mvz",
    title: "Cyberversicherung für MVZ - Umfassender Schutz für große Einrichtungen",
    metaDescription: "Spezialisierte Cyberversicherung für Medizinische Versorgungszentren ab 150€/Monat. Schutz für komplexe IT-Infrastrukturen, Mehrfachstandorte und große Patientendatenbanken.",
    heroTitle: "Cyberversicherung für Medizinische Versorgungszentren",
    heroSubtitle: "Professioneller Schutz für komplexe IT-Infrastrukturen und Mehrfachstandorte ab 150€/Monat",
    description: "MVZ verwalten Patientendaten von tausenden Patienten über mehrere Standorte und Fachrichtungen hinweg. Die IT-Infrastruktur ist komplex, vernetzt und damit besonders anfällig für Cyberangriffe. Ein Ausfall kann katastrophale Folgen haben.",
    specificRisks: [
      {
        icon: "Building2",
        title: "Mehrfachstandort-Ausfall",
        description: "Ein Cyberangriff kann alle vernetzten Standorte gleichzeitig lahmlegen. Der Schaden potenziert sich durch die Größe der Einrichtung.",
        statistic: "Ø 250.000€ Schaden bei 5 Standorten"
      },
      {
        icon: "Database",
        title: "Große Patientendatenbanken",
        description: "MVZ verwalten oft 10.000+ Patientenakten digital. Ein Datenleck führt zu massiven DSGVO-Bußgeldern und Schadensersatzforderungen.",
        statistic: "Bis zu 4% des Jahresumsatzes Bußgeld"
      },
      {
        icon: "Network",
        title: "Komplexe Netzwerk-Infrastruktur",
        description: "Vernetzte Standorte, zentrale Server, Cloud-Systeme und mobile Endgeräte bieten viele Einfallstore für Cyberkriminelle.",
        statistic: "3x höheres Angriffsrisiko als Einzelpraxen"
      },
      {
        icon: "Users",
        title: "Viele Nutzer-Accounts",
        description: "Mit 20+ Mitarbeitern steigt das Risiko durch Phishing, schwache Passwörter oder Social Engineering exponentiell.",
        statistic: "85% Angriffe über Mitarbeiter-Accounts"
      },
      {
        icon: "Server",
        title: "Zentrale Server-Abhängigkeit",
        description: "Ausfall des zentralen Servers bedeutet Stillstand aller Standorte. Backup-Systeme werden oft mitangegriffen.",
        statistic: "Ø 21 Tage bis zur Wiederherstellung"
      },
      {
        icon: "FileWarning",
        title: "Regulatorische Anforderungen",
        description: "MVZ unterliegen strengeren Auflagen als Einzelpraxen. Datenpannen führen zu behördlichen Untersuchungen und höheren Strafen.",
        statistic: "Ø 125.000€ behördliche Verfahrenskosten"
      }
    ],
    recommendedCoverage: [
      {
        coverage: "Cyber-Eigenschaden",
        amount: "500.000 - 2.000.000 €",
        importance: "essential",
        description: "Für große IT-Infrastruktur und Mehrfachstandorte"
      },
      {
        coverage: "Betriebsunterbrechung",
        amount: "300.000 - 1.000.000 €",
        importance: "essential",
        description: "Hohe Fixkosten und Umsatzausfälle mehrerer Standorte"
      },
      {
        coverage: "Cyber-Haftpflicht",
        amount: "5.000.000 - 10.000.000 €",
        importance: "essential",
        description: "Schutz vor massiven DSGVO-Strafen bei großen Datenbanken"
      },
      {
        coverage: "Cyber-Erpressung",
        amount: "100.000 - 500.000 €",
        importance: "essential",
        description: "MVZ sind beliebte Ziele wegen hoher Zahlungsfähigkeit"
      },
      {
        coverage: "Krisenmanagement & PR",
        amount: "100.000 - 250.000 €",
        importance: "recommended",
        description: "Reputationsschäden bei großen Einrichtungen abfedern"
      },
      {
        coverage: "Rechtsschutz Cyber",
        amount: "150.000 - 300.000 €",
        importance: "recommended",
        description: "Komplexe rechtliche Auseinandersetzungen"
      }
    ],
    caseStudies: [
      {
        title: "MVZ Rhein-Main - Ransomware auf 7 Standorte",
        description: "Ransomware verbreitete sich über das zentrale Netzwerk auf alle 7 Standorte. 18.000 Patientenakten verschlüsselt, alle Fachrichtungen betroffen.",
        damage: "385.000 € (IT-Forensik, Datenrettung, 18 Tage Teilausfall, DSGVO-Meldung, Patienteninformation, Krisenmanagement)",
        solution: "Cyberversicherung koordinierte Notfall-Task-Force mit IT-Forensikern, übernahm alle Kosten inkl. Betriebsunterbrechung für 7 Standorte."
      },
      {
        title: "MVZ Nord - Zentral-Server gehackt",
        description: "Hacker verschafften sich über VPN-Schwachstelle Zugang zum zentralen Server. Patientendaten von 12.500 Patienten gestohlen.",
        damage: "520.000 € (Forensik, Behördenverfahren, DSGVO-Bußgeld 180.000€, Anwaltskosten, neue IT-Sicherheit, PR-Krisenmanagement)",
        solution: "Versicherung deckte Bußgelder, stellte Datenschutz-Anwälte und PR-Experten bereit, finanzierte komplettes IT-Sicherheitsupgrade."
      }
    ],
    faqs: [
      {
        question: "Warum benötigen MVZ eine spezielle Cyberversicherung?",
        answer: "MVZ haben eine deutlich komplexere IT-Infrastruktur als Einzelpraxen, verwalten größere Datenmengen über mehrere Standorte und unterliegen strengeren regulatorischen Anforderungen. Standard-Cyberversicherungen für Arztpraxen decken diese besonderen Risiken nicht ausreichend ab."
      },
      {
        question: "Was kostet eine Cyberversicherung für ein MVZ?",
        answer: "Die Kosten hängen stark von Faktoren wie Anzahl der Standorte, Mitarbeiterzahl, IT-Infrastruktur und Patientenzahl ab. Kleine MVZ mit 2-3 Standorten zahlen ab 150€ monatlich, größere Einrichtungen mit 5+ Standorten und 50+ Mitarbeitern zwischen 400€ und 1.200€ pro Monat."
      },
      {
        question: "Wie hoch sollte die Deckungssumme für ein MVZ sein?",
        answer: "Wir empfehlen mindestens 500.000€ für Eigenschäden und 5.000.000€ für Haftpflichtschäden. Bei MVZ mit mehr als 10.000 Patienten oder 5+ Standorten sollten die Summen auf 2.000.000€ bzw. 10.000.000€ erhöht werden."
      },
      {
        question: "Sind alle Standorte automatisch mitversichert?",
        answer: "Ja, bei korrekter Deklaration sind alle Standorte des MVZ mitversichert. Wichtig ist, dass Sie bei Eröffnung neuer Standorte oder Übernahme anderer Praxen Ihre Versicherung umgehend informieren, damit der Schutz nahtlos greift."
      },
      {
        question: "Was passiert bei einem Angriff, der mehrere Standorte betrifft?",
        answer: "Die Versicherung aktiviert ein koordiniertes Notfall-Team aus IT-Forensikern, das parallel an allen betroffenen Standorten arbeitet. Die Betriebsunterbrechungsdeckung greift für alle Standorte gleichzeitig."
      },
      {
        question: "Werden auch externe Dienstleister (Abrechnungsservice, Cloud-Provider) abgedeckt?",
        answer: "Schäden durch Ausfall oder Sicherheitsvorfälle bei externen Dienstleistern sind versichert, sofern diese im Rahmen Ihres Praxisbetriebs genutzt werden. Die Dienstleister selbst benötigen jedoch eigene Versicherungen."
      }
    ],
    priceRange: "150-1200",
    employeeRange: "20-150",
    practiceCount: "3.200+"
  },
  
  klinik: {
    name: "Kliniken & Krankenhäuser",
    slug: "klinik",
    title: "Cyberversicherung für Kliniken - Maximaler Schutz für Krankenhäuser",
    metaDescription: "Enterprise-Cyberversicherung für Kliniken und Krankenhäuser. Schutz für kritische Infrastrukturen, Medizingeräte-Netzwerke und Patientendatenbanken. Individuelle Beratung.",
    heroTitle: "Cyberversicherung für Kliniken & Krankenhäuser",
    heroSubtitle: "Enterprise-Schutz für kritische medizinische Infrastrukturen - Individuelle Lösungen",
    description: "Krankenhäuser und Kliniken sind hochkomplexe Organisationen mit kritischer IT-Infrastruktur. Ein Cyberangriff kann lebensbedrohliche Situationen verursachen und Millionenschäden anrichten. Enterprise-Level-Cyberversicherungen bieten maximalen Schutz.",
    specificRisks: [
      {
        icon: "Activity",
        title: "Lebenserhaltende Systeme",
        description: "Cyberangriffe auf vernetzte Medizingeräte wie Beatmungsgeräte, Infusionspumpen oder OP-Roboter können Leben gefährden.",
        statistic: "67% der Kliniken bereits angegriffen"
      },
      {
        icon: "Database",
        title: "Massive Patientendatenbanken",
        description: "Hundertausende Patientenakten, Labordaten, Röntgenbilder und OP-Berichte sind hochattraktive Ziele für Cyberkriminelle.",
        statistic: "Ø 500.000+ Datensätze pro Klinik"
      },
      {
        icon: "AlertCircle",
        title: "Notaufnahme-Ausfall",
        description: "IT-Ausfall kann zur Schließung der Notaufnahme führen. Patienten müssen umgeleitet werden, im Notfall lebensbedrohlich.",
        statistic: "Ø 4,5 Mio. € Schaden bei 3-Tage-Ausfall"
      },
      {
        icon: "Network",
        title: "Kritische Infrastruktur",
        description: "Kliniken gelten als KRITIS. Cyberangriffe können zu behördlichen Untersuchungen, Auflagen und massiven Strafen führen.",
        statistic: "BSI-KRITIS-Auflagen verpflichtend"
      },
      {
        icon: "Building",
        title: "Gebäudetechnik-Angriffe",
        description: "Vernetzte Gebäudetechnik (Klima, Aufzüge, Zugang) kann durch Cyberangriffe sabotiert werden und den Klinikbetrieb stören.",
        statistic: "Ø 85.000€ Reparaturkosten"
      },
      {
        icon: "Shield",
        title: "Reputationsschäden",
        description: "Datenpannen in Kliniken führen zu massivem Vertrauensverlust, Patientenabwanderung und langfristigen Umsatzeinbußen.",
        statistic: "Ø -18% Patientenzahlen im Folgejahr"
      }
    ],
    recommendedCoverage: [
      {
        coverage: "Cyber-Eigenschaden",
        amount: "5.000.000 - 20.000.000 €",
        importance: "essential",
        description: "Für umfassende IT-Infrastruktur und Medizintechnik"
      },
      {
        coverage: "Betriebsunterbrechung",
        amount: "2.000.000 - 10.000.000 €",
        importance: "essential",
        description: "Massive Umsatzausfälle bei Notaufnahme-Schließung"
      },
      {
        coverage: "Cyber-Haftpflicht",
        amount: "10.000.000 - 50.000.000 €",
        importance: "essential",
        description: "KRITIS-Anforderungen, massive DSGVO-Risiken"
      },
      {
        coverage: "Cyber-Erpressung",
        amount: "500.000 - 5.000.000 €",
        importance: "essential",
        description: "Kliniken sind Hauptziele für Ransomware-Angriffe"
      },
      {
        coverage: "Krisenmanagement & PR",
        amount: "250.000 - 1.000.000 €",
        importance: "essential",
        description: "Reputationsschutz bei öffentlichkeitswirksamen Vorfällen"
      },
      {
        coverage: "Medizingeräte-Wiederherstellung",
        amount: "500.000 - 2.000.000 €",
        importance: "recommended",
        description: "Spezialisierte Forensik für Medizintechnik"
      }
    ],
    caseStudies: [
      {
        title: "Universitätsklinik - Ransomware legt OP-Betrieb lahm",
        description: "Ransomware verschlüsselte das Kliniknetzwerk inkl. OP-Planungssystem. 28 geplante OPs mussten verschoben werden, Notaufnahme 48h geschlossen.",
        damage: "4,2 Mio. € (IT-Forensik, Systemwiederherstellung, Betriebsunterbrechung, Patientenverlegungen, Krisenmanagement, Behördenverfahren)",
        solution: "Enterprise-Cyberversicherung aktivierte 24/7-Task-Force mit Medizintechnik-Spezialisten, übernahm alle Kosten inkl. Patientenentschädigungen."
      },
      {
        title: "Kreiskrankenhaus - Patientendaten-Diebstahl",
        description: "Hacker stahlen über VPN-Schwachstelle 127.000 Patientendatensätze inkl. Diagnosen, OP-Berichten und Labordaten.",
        damage: "2,8 Mio. € (Forensik, DSGVO-Bußgeld 1,2 Mio.€, Anwaltskosten, Patienteninformation, IT-Sicherheitsupgrade, PR-Krise)",
        solution: "Versicherung deckte Bußgelder, stellte Datenschutz-Taskforce und Krisenmanagement-Team, finanzierte IT-Komplettsanierung."
      }
    ],
    faqs: [
      {
        question: "Warum benötigen Kliniken eine spezielle Enterprise-Cyberversicherung?",
        answer: "Kliniken sind kritische Infrastrukturen (KRITIS) mit hochkomplexen IT-Systemen, lebenserhaltender Medizintechnik und massiven Datenmengen. Die potenziellen Schäden gehen in die Millionen. Standard-Cyberversicherungen decken diese Größenordnung und Komplexität nicht ab."
      },
      {
        question: "Was kostet eine Cyberversicherung für Krankenhäuser?",
        answer: "Die Kosten werden individuell kalkuliert basierend auf Bettenzahl, IT-Infrastruktur, Fachbereichen und Risikoanalyse. Kleine Krankenhäuser (100-200 Betten) zahlen ab 1.500€ monatlich, große Universitätskliniken zwischen 8.000€ und 25.000€ pro Monat."
      },
      {
        question: "Welche Deckungssummen sind für Kliniken notwendig?",
        answer: "Wir empfehlen mindestens 5.000.000€ für Eigenschäden und 10.000.000€ für Haftpflicht. Große Kliniken (500+ Betten) oder Universitätskliniken sollten 20.000.000€ bzw. 50.000.000€ Deckung haben."
      },
      {
        question: "Sind auch Schäden an Medizingeräten durch Cyberangriffe versichert?",
        answer: "Ja, moderne Enterprise-Cyberversicherungen decken auch Schäden an vernetzter Medizintechnik ab. Dazu gehören Reparaturkosten, Datenwiederherstellung und Kosten für Ersatzgeräte während der Ausfallzeit."
      },
      {
        question: "Was passiert, wenn die Notaufnahme durch Cyberangriff geschlossen werden muss?",
        answer: "Die Betriebsunterbrechungsversicherung greift sofort. Alle Umsatzausfälle, Kosten für Patientenverlegungen und zusätzliche Maßnahmen werden erstattet. Zudem unterstützt die Versicherung bei der Koordination mit Behörden und anderen Kliniken."
      },
      {
        question: "Wie schnell reagiert die Versicherung im Notfall?",
        answer: "Enterprise-Cyberversicherungen bieten 24/7-Notfall-Hotlines mit maximal 1 Stunde Reaktionszeit. Ein spezialisiertes Task-Force-Team aus IT-Forensikern, Medizintechnik-Experten und Krisenmanagern wird sofort aktiviert."
      }
    ],
    priceRange: "1500-25000",
    employeeRange: "100-5000",
    practiceCount: "1.900+"
  },
  
  facharzt: {
    name: "Facharztpraxen",
    slug: "facharzt",
    title: "Cyberversicherung für Facharztpraxen - Spezialisierter Schutz",
    metaDescription: "Cyberversicherung für Fachärzte ab 32€/Monat. Spezieller Schutz für Dermatologen, Radiologen, Orthopäden, Gynäkologen, Kardiologen. Jetzt beraten lassen.",
    heroTitle: "Cyberversicherung für Facharztpraxen",
    heroSubtitle: "Spezialisierter Schutz für Fachrichtungen mit besonderen IT-Anforderungen ab 32€/Monat",
    description: "Facharztpraxen haben je nach Fachrichtung unterschiedliche IT-Anforderungen und Risiken: Radiologen mit großen Bilddatenbanken, Dermatologen mit Fotodokumentationen, Kardiologen mit Diagnosegeräten. Unsere Cyberversicherung berücksichtigt diese Besonderheiten.",
    specificRisks: [
      {
        icon: "Microscope",
        title: "Fachspezifische Datenbanken",
        description: "Je nach Fachrichtung: Große Bilddatenbanken (Radiologie, Dermatologie), EKG-Daten (Kardiologie), OP-Dokumentationen (Chirurgie).",
        statistic: "Ø 45GB Daten pro Facharztpraxis"
      },
      {
        icon: "Stethoscope",
        title: "Diagnosegeräte-Vernetzung",
        description: "Moderne Diagnosegeräte (MRT, CT, Ultraschall, EKG) sind vernetzt und können durch Cyberangriffe kompromittiert werden.",
        statistic: "72% der Geräte netzwerkfähig"
      },
      {
        icon: "FileText",
        title: "Spezialisierte Abrechnungssysteme",
        description: "Fachärzte nutzen oft spezialisierte Abrechnungssoftware mit höherer Komplexität. Ausfall führt zu massiven Verzögerungen.",
        statistic: "Ø 18.000€ Honorarverzug pro Woche"
      },
      {
        icon: "Image",
        title: "Bildgebende Verfahren",
        description: "Röntgen, MRT, CT, Ultraschall - große Datenmengen und hohe Anforderungen an Datensicherheit und Verfügbarkeit.",
        statistic: "Ø 2.500 Bilddateien pro Monat"
      },
      {
        icon: "Calendar",
        title: "Online-Terminbuchung",
        description: "Viele Facharztpraxen bieten Online-Terminbuchung. Hackerangriffe können Terminkalender manipulieren oder löschen.",
        statistic: "38% nutzen Online-Buchungssysteme"
      },
      {
        icon: "Users2",
        title: "Überweisungsnetzwerk",
        description: "Intensive digitale Kommunikation mit zuweisenden Ärzten, Kliniken und Laboren. Kompromittierte Kommunikation gefährdet Patientenversorgung.",
        statistic: "Ø 120 Überweisungen pro Woche"
      }
    ],
    recommendedCoverage: [
      {
        coverage: "Cyber-Eigenschaden",
        amount: "100.000 - 500.000 €",
        importance: "essential",
        description: "Je nach Fachrichtung und Geräteausstattung"
      },
      {
        coverage: "Betriebsunterbrechung",
        amount: "50.000 - 250.000 €",
        importance: "essential",
        description: "Höher bei bildgebenden Fachrichtungen"
      },
      {
        coverage: "Cyber-Haftpflicht",
        amount: "1.000.000 - 5.000.000 €",
        importance: "essential",
        description: "Große Datenmengen = höheres DSGVO-Risiko"
      },
      {
        coverage: "Bilddaten-Wiederherstellung",
        amount: "50.000 - 200.000 €",
        importance: "recommended",
        description: "Speziell für Radiologie, Dermatologie"
      },
      {
        coverage: "Cyber-Erpressung",
        amount: "25.000 - 150.000 €",
        importance: "recommended",
        description: "Fachärzte oft lukrative Ziele"
      },
      {
        coverage: "Geräte-Wiederinbetriebnahme",
        amount: "25.000 - 100.000 €",
        importance: "optional",
        description: "Für vernetzte Diagnosegeräte"
      }
    ],
    caseStudies: [
      {
        title: "Radiologiepraxis - MRT-System gehackt",
        description: "Ransomware verschlüsselte PACS-System mit 12.000 MRT/CT-Aufnahmen. Befundung unmöglich, Patienten mussten neu untersucht werden.",
        damage: "78.000 € (Forensik, Datenrettung, 11 Tage Ausfall, Neuuntersuchungen, Patientenentschädigungen)",
        solution: "Cyberversicherung stellte medizintechnische IT-Spezialisten, übernahm alle Kosten inkl. Neuuntersuchungen."
      },
      {
        title: "Dermatologie - Fotodatenbank gestohlen",
        description: "Hacker stahlen 8.500 Patientenfotos (Hautkrebsvorsorge, OP-Dokumentationen). Massive DSGVO-Verletzung.",
        damage: "52.000 € (Forensik, DSGVO-Meldung, Anwaltskosten, Patienteninformation, Krisenmanagement)",
        solution: "Versicherung deckte alle Kosten, stellte Datenschutz-Anwälte und PR-Berater für Krisenkommunikation."
      }
    ],
    faqs: [
      {
        question: "Unterscheidet sich die Cyberversicherung je nach Fachrichtung?",
        answer: "Ja, die Anforderungen variieren stark: Radiologen benötigen höhere Deckungssummen für Bilddaten, Kardiologen für Diagnosegeräte, Chirurgen für OP-Dokumentationen. Wir passen die Versicherung individuell an Ihre Fachrichtung an."
      },
      {
        question: "Was kostet eine Cyberversicherung für Fachärzte?",
        answer: "Die Kosten hängen von der Fachrichtung ab. Internisten oder Allgemeinmediziner zahlen ab 32€ monatlich, bildgebende Fachrichtungen (Radiologie, Kardiologie) zwischen 85€ und 250€ pro Monat wegen höherer Datenmengen und teurerer Geräte."
      },
      {
        question: "Sind auch externe Befundungsplattformen versichert?",
        answer: "Ja, Schäden durch Ausfall oder Sicherheitsvorfälle bei externen Befundungsplattformen oder Teleradiologie-Diensten sind versichert, sofern diese im Rahmen Ihrer Praxistätigkeit genutzt werden."
      },
      {
        question: "Was passiert, wenn bildgebende Geräte durch Cyberangriff ausfallen?",
        answer: "Die Versicherung übernimmt Kosten für IT-Forensik an Medizingeräten, Wiederinbetriebnahme, Datenrettung und bei Bedarf Mietkosten für Ersatzgeräte während der Ausfallzeit."
      },
      {
        question: "Wie hoch sollte die Deckungssumme für meine Fachrichtung sein?",
        answer: "Das hängt von Ihrer Fachrichtung ab: Internisten/Gynäkologen 100.000-150.000€, Orthopäden/Dermatologen 150.000-250.000€, Radiologen/Kardiologen 250.000-500.000€ für Eigenschäden. Haftpflicht mindestens 1.000.000€, bei bildgebenden Verfahren 3.000.000-5.000.000€."
      },
      {
        question: "Werden Schulungen für Mitarbeiter von der Versicherung unterstützt?",
        answer: "Viele Versicherer bieten im Rahmen der Cyberversicherung kostenlose Cyber-Security-Awareness-Schulungen für Praxisteams an, um Risiken durch Phishing und Social Engineering zu minimieren."
      }
    ],
    priceRange: "32-250",
    employeeRange: "2-15",
    practiceCount: "35.000+"
  }
};

export const getPracticeType = (slug: string): PracticeTypeData | undefined => {
  return practiceTypes[slug];
};

export const getAllPracticeTypes = (): PracticeTypeData[] => {
  return Object.values(practiceTypes);
};
